import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import {
  FiUsers,
  FiClipboard,
  FiLogOut,
  FiBriefcase,
  FiHome,
  FiX,
  FiMenu,
  FiChevronLeft,
  FiChevronRight,
  FiDollarSign,
  FiSettings,
  FiMail,
  FiActivity,
} from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import spformsApi from '../lib/spformsApi';

const SIDEBAR_STORAGE_KEY = 'spforms_sidebar_collapsed';

function readSidebarCollapsed() {
  try {
    return localStorage.getItem(SIDEBAR_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

function writeSidebarCollapsed(value) {
  try {
    localStorage.setItem(SIDEBAR_STORAGE_KEY, String(value));
  } catch {
    // ignore
  }
}

export default function AdminLayout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, session } = useAuth();
  const {
    tenantId,
    tenantOverride,
    setTenantOverride,
    clearTenantOverride,
    isSuperadmin,
  } = useTenant();

  const [tenantOptions, setTenantOptions] = useState([]);
  const [collapsed, setCollapsed] = useState(readSidebarCollapsed);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    writeSidebarCollapsed(collapsed);
  }, [collapsed]);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    let mounted = true;
    async function loadTenantOptions() {
      if (!isSuperadmin) return;
      try {
        const data = await spformsApi.adminTenantsList();
        if (mounted) setTenantOptions(data);
      } catch {
        if (mounted) setTenantOptions([]);
      }
    }
    loadTenantOptions();
    return () => {
      mounted = false;
    };
  }, [isSuperadmin]);

  const navItems = [
    { name: 'Dashboard', path: '/dashboard', icon: FiHome },
    ...(isSuperadmin
      ? [
          { name: 'Tenants', path: '/tenants', icon: FiBriefcase },
          { name: 'Tenant Users', path: '/tenant-users', icon: FiUsers },
          { name: 'Global Settings', path: '/settings', icon: FiSettings },
        ]
      : []),
    { name: 'Events', path: '/intake-groups', icon: FiClipboard },
    { name: 'Participants', path: '/participants', icon: FiUsers },
    { name: 'Email Templates', path: '/email-templates', icon: FiMail },
    { name: 'Email Logs', path: '/email-logs', icon: FiActivity },
    { name: 'Finances', path: '/finances', icon: FiDollarSign },
  ];

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  const sidebarWidth = collapsed ? 'w-24' : 'w-72';

  const sidebarContent = (
    <div
      className={`${sidebarWidth} bg-white border-r border-gray-200 flex flex-col shrink-0 transition-all duration-300 h-full`}
    >
      <div className="px-4 py-6 border-b border-gray-200 flex items-center justify-between gap-2">
        {!collapsed ? (
          <h1 className="text-2xl font-black text-indigo-700 tracking-tight truncate">
            Sportiko<span className="text-indigo-400">Forms</span>
          </h1>
        ) : (
          <div className="w-12 h-12 rounded-2xl bg-indigo-600 text-white flex items-center justify-center font-black">
            SF
          </div>
        )}
        <button
          type="button"
          onClick={() => setCollapsed((prev) => !prev)}
          className="hidden lg:flex w-10 h-10 items-center justify-center rounded-xl bg-gray-50 text-gray-500 hover:bg-indigo-50 hover:text-indigo-600 transition-all"
          title={collapsed ? 'Expand menu' : 'Collapse menu'}
        >
          <SafeIcon icon={collapsed ? FiChevronRight : FiChevronLeft} />
        </button>
        <button
          type="button"
          onClick={() => setMobileOpen(false)}
          className="lg:hidden w-10 h-10 flex items-center justify-center rounded-xl bg-gray-50 text-gray-500 hover:bg-red-50 hover:text-red-600 transition-all"
        >
          <SafeIcon icon={FiX} />
        </button>
      </div>

      {!collapsed ? (
        <div className="px-4 py-6 border-b border-gray-200 bg-gray-50/50">
          <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">
            User Context
          </div>
          <div className="text-sm font-bold text-gray-800 truncate">
            {session?.email || '-'}
          </div>
          <div className="mt-2 text-[10px] font-bold text-gray-400 uppercase tracking-widest">
            Tenant
          </div>
          <div className="mt-1 text-xs font-bold text-gray-600 break-all">
            {tenantId || 'No tenant selected'}
          </div>

          {isSuperadmin ? (
            <div className="mt-4 space-y-3">
              <div className="text-[10px] font-black text-indigo-600 uppercase tracking-widest">
                Superadmin Context
              </div>
              <select
                value={tenantOverride || ''}
                onChange={(e) => setTenantOverride(e.target.value)}
                className="w-full rounded-xl border border-gray-200 px-3 py-2 text-xs font-bold focus:ring-2 focus:ring-indigo-500 outline-none"
              >
                <option value="">No tenant selected</option>
                {tenantOptions.map((tenant) => (
                  <option key={tenant.id} value={tenant.id}>
                    {tenant.name}
                  </option>
                ))}
              </select>
              {tenantOverride ? (
                <button
                  type="button"
                  onClick={clearTenantOverride}
                  className="w-full flex items-center justify-center gap-2 py-2 text-[10px] font-black text-red-500 uppercase hover:bg-red-50 rounded-lg transition-colors"
                >
                  <SafeIcon icon={FiX} /> Reset Context
                </button>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}

      <nav className="flex-1 px-4 py-6 space-y-1">
        {navItems.map((item) => {
          const isActive =
            location.pathname === item.path ||
            location.pathname.startsWith(`${item.path}/`);

          return (
            <Link
              key={item.name}
              to={item.path}
              title={collapsed ? item.name : undefined}
              className={`flex items-center ${
                collapsed ? 'justify-center px-0' : 'gap-3 px-4'
              } py-3 rounded-xl text-sm font-bold transition-all ${
                isActive
                  ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-100'
                  : 'text-gray-500 hover:bg-gray-100 hover:text-gray-900'
              }`}
            >
              <SafeIcon icon={item.icon} size={18} />
              {!collapsed ? <span>{item.name}</span> : null}
            </Link>
          );
        })}
      </nav>

      <div className="p-4 border-t border-gray-200">
        <button
          type="button"
          onClick={handleLogout}
          title={collapsed ? 'Sign Out' : undefined}
          className={`w-full flex items-center justify-center ${
            collapsed ? '' : 'gap-2'
          } py-3 rounded-xl text-sm font-black text-gray-500 hover:bg-red-50 hover:text-red-600 transition-all`}
        >
          <SafeIcon icon={FiLogOut} />
          {!collapsed ? <span>Sign Out</span> : null}
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar for Desktop */}
      <div className="hidden lg:flex">{sidebarContent}</div>

      {/* Sidebar for Mobile */}
      {mobileOpen ? (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setMobileOpen(false)}
          />
          <div className="relative h-full">{sidebarContent}</div>
        </div>
      ) : null}

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        <header className="lg:hidden h-16 bg-white border-b border-gray-200 flex items-center justify-between px-4">
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            className="w-11 h-11 flex items-center justify-center rounded-xl bg-gray-50 text-gray-600"
          >
            <SafeIcon icon={FiMenu} />
          </button>
          <div className="text-lg font-black text-indigo-700">
            Sportiko<span className="text-indigo-400">Forms</span>
          </div>
          <div className="w-11" />
        </header>

        <main className="flex-1 overflow-auto p-5 lg:p-10">{children}</main>
      </div>
    </div>
  );
}